import type { ButtonHTMLAttributes, ReactNode } from 'react';

import { cn } from '@/lib/cn';
import type { DangerTier } from '@/lib/permissions';

import { dangerTierConfig } from './danger-tiers';

interface DangerButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  tier: DangerTier;
  children: ReactNode;
  loading?: boolean;
  /** Tier 3 buttons are outline-only by default; a filled variant is used inside
   *  the confirmation sheet where the action is already fully contextualised. */
  variant?: 'outline' | 'filled';
  fullWidth?: boolean;
  className?: string;
}

/**
 * The single button component for every mutating action in the console.
 *
 * Tier 3 is never filled red in a list of actions — a wall of solid red trains
 * operators to ignore it. It reads as an outlined, clearly-marked control in
 * context, and only becomes solid at the final confirm step where it is the one
 * thing on screen.
 */
export function DangerButton({
  tier,
  children,
  loading = false,
  variant = 'outline',
  fullWidth = false,
  disabled,
  ...rest
}: DangerButtonProps) {
  const config = dangerTierConfig(tier);
  const Icon = config.icon;
  const isDisabled = disabled === true || loading;
  const filled = variant === 'filled';

  return (
    <button
      type="button"
      disabled={isDisabled}
      aria-busy={loading}
      // 44px min height: these are the controls most likely to be tapped on a phone.
      className={cn(
        'inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border px-3.5 text-[13px] font-medium transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-45',
        fullWidth && 'w-full',
      )}
      style={{
        background: filled ? config.accent : config.surface,
        borderColor: filled ? config.accent : config.border,
        color: filled ? config.onAccent : tier === 1 ? 'var(--tx)' : config.accentSoft,
      }}
      {...rest}
    >
      {loading ? (
        <span
          aria-hidden
          className="motion-safe:animate-mesh-spin size-3.5 flex-none rounded-full border-2 border-current border-t-transparent"
        />
      ) : (
        tier > 1 && <Icon size={14} className="flex-none" aria-hidden />
      )}
      <span>{children}</span>
    </button>
  );
}

/**
 * Groups actions by tier with a labelled heading.
 *
 * This exists to enforce the brief's adjacency rule: a Tier 3 control must never
 * sit next to a Tier 1 control. Rendering actions through tier-labelled groups
 * makes that structural rather than a thing each screen has to remember.
 */
export function ActionGroup({
  tier,
  hint,
  children,
}: {
  tier: DangerTier;
  /** Overrides the default group hint, e.g. "bypasses the lock". */
  hint?: string;
  children: ReactNode;
}) {
  const config = dangerTierConfig(tier);

  return (
    <section
      className="rounded-xl border p-3.5"
      style={{
        borderColor: tier === 3 ? config.border : 'var(--bd)',
        background: tier === 3 ? 'rgba(var(--err-rgb), 0.04)' : 'var(--card)',
      }}
    >
      <div className="mb-2.5 flex items-center gap-2">
        {tier > 1 && (
          <config.icon
            size={13}
            style={{ color: config.accent }}
            aria-hidden
            className="flex-none"
          />
        )}
        <h3
          className="font-head text-[12.5px] font-semibold uppercase"
          style={{
            letterSpacing: '0.14em',
            color: tier === 1 ? 'var(--tx2)' : config.accentSoft,
          }}
        >
          {tier === 1 ? 'Safe & ordinary' : `Tier ${String(tier)}`}
        </h3>
        <span className="text-tx3 font-mono text-[10.5px]">· {hint ?? config.groupHint}</span>
      </div>
      <div className="flex flex-wrap gap-2">{children}</div>
    </section>
  );
}
